/**
 * Seedance 样片（Draft）转正式视频。
 *
 * 官方流程：Step 1 以 draft=true 生成 480p 样片；Step 2 用样片任务 ID 再次创建任务，
 * content 仅传 { type: 'draft_task', draft_task: { id } }，模型自动复用样片的提示词、素材、
 * 时长、比例、种子、音频设置与任务类型（重复传入会报错），resolution 仅支持 1080p。
 * 样片任务 ID 仅在创建它的账号/接入点下有效，有效期 7 天。
 */

/** 样片模型 → 对应的正式模型（节点层面的 modelId） */
export const DRAFT_FORMAL_MODEL: Record<string, string> = {
    'seedance-2.5-draft': 'seedance-2.5',
};

/** 样片渠道 → 同账号/同接入点的正式渠道，key/value 均为 `${gateway}:${providerModelId}` */
export const DRAFT_FORMAL_CHANNEL: Record<string, string> = {
    'baidu:seedance-2.5-api-draft-real': 'baidu:seedance-2.5-api-real',
    'volcengine:seedance-2.5-api-draft': 'volcengine:seedance-2.5-api',
    'volcengine:seedance-2.5-api-draft-beijing': 'volcengine:seedance-2.5-api-beijing',
};

/** 基于样片生成正式视频时唯一支持的分辨率 */
export const DRAFT_FORMAL_QUALITY = '1080p';

/** 样片任务 ID 有效期（从样片任务创建时起算） */
export const DRAFT_TASK_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * 服务端解析后注入 providerOptions 的样片任务信息；worker 据此锁定渠道，buildInput 据此改写请求体。
 * 只能由服务端写入，入口处需用 stripDraftTaskOption 清掉客户端传入的同名字段。
 */
export type DraftTaskOption = {
    id: string;
    gateway: string;
    modelId: string;
};

export const DRAFT_TASK_OPTION_KEY = '_draftTask';

export function getFormalModelForDraft(modelId: string | undefined): string | undefined {
    return modelId ? DRAFT_FORMAL_MODEL[modelId] : undefined;
}

export function getDraftTaskOption(providerOptions: unknown): DraftTaskOption | undefined {
    const raw = (providerOptions as any)?.[DRAFT_TASK_OPTION_KEY];
    if (!raw || typeof raw.id !== 'string' || !raw.id) return undefined;
    return raw as DraftTaskOption;
}

export function stripDraftTaskOption<T extends Record<string, any> | undefined>(providerOptions: T): T {
    if (!providerOptions || !(DRAFT_TASK_OPTION_KEY in providerOptions)) return providerOptions;
    const { [DRAFT_TASK_OPTION_KEY]: _ignored, ...rest } = providerOptions;
    return rest as T;
}

/** 由样片实际使用的渠道得到正式渠道；不支持转正式的渠道返回 undefined */
export function resolveDraftFormalChannel(
    gateway: string | undefined,
    providerModelId: string | undefined,
): { gateway: string; modelId: string } | undefined {
    if (!gateway || !providerModelId) return undefined;
    const formal = DRAFT_FORMAL_CHANNEL[`${gateway.toLowerCase()}:${providerModelId}`];
    if (!formal) return undefined;
    const [formalGateway, formalModelId] = formal.split(':');
    return { gateway: formalGateway, modelId: formalModelId };
}

/**
 * 正式渠道配置包装：providerOptions 带样片任务时，请求体只保留 model 与可重新指定的参数。
 * model 取自原 buildInput（各 frameMode 接入点可能不同，需与样片一致，样片沿用同一 buildInput）。
 */
export function withDraftTask<T extends { buildInput: (params: any) => any }>(config: T): T {
    return {
        ...config,
        buildInput: async (params: any) => {
            const built = await config.buildInput(params);
            const draftTask = getDraftTaskOption(params?.providerOptions);
            if (!draftTask) return built;
            return {
                model: built.model,
                content: [{ type: 'draft_task', draft_task: { id: draftTask.id } }],
                resolution: DRAFT_FORMAL_QUALITY,
                watermark: built.watermark,
                ...(built.output_format ? { output_format: built.output_format } : {}),
            };
        },
    };
}
